import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Network, TriangleAlert, Crosshair, Globe2, ShieldAlert, RefreshCw, Zap } from "lucide-react";

const QUADRANTS = {
  strategic: { label: "Strategic", cls: "border-ek-purple/30 bg-ek-purple/[0.06]", dot: "bg-ek-purple", desc: "High impact · high risk — protect & dual-source" },
  leverage: { label: "Leverage", cls: "border-emerald-200 bg-emerald-50/60", dot: "bg-emerald-500", desc: "High impact · low risk — consolidate spend" },
  bottleneck: { label: "Bottleneck", cls: "border-amber-200 bg-amber-50/60", dot: "bg-amber-500", desc: "Low impact · high risk — secure supply" },
  routine: { label: "Routine", cls: "border-black/10 bg-white", dot: "bg-ek-ink/30", desc: "Low impact · low risk — simplify" },
};

const compositeColor = (n) =>
  n >= 70 ? "text-red-600" : n >= 45 ? "text-amber-600" : "text-emerald-600";

export const SupplyChainIntel = ({ onSelect, canRaise }) => {
  const [data, setData] = useState(null);
  const [autoRaised, setAutoRaised] = useState(null);
  const [scanning, setScanning] = useState(false);

  const loadAutoRaised = () =>
    api.get("/suppliers/auto-raised-risks").then(({ data }) => setAutoRaised(data)).catch(() => {});

  useEffect(() => {
    api.get("/suppliers/analytics").then(({ data }) => setData(data)).catch(() => {});
    loadAutoRaised();
  }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/suppliers/auto-raise-risks");
      const n = data.raised?.length || 0;
      toast.success(
        n > 0
          ? `${n} new third-party risk${n > 1 ? "s" : ""} auto-raised`
          : "Scan complete — no new risks crossed the threshold"
      );
      loadAutoRaised();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  if (!data) return null;
  const byQuad = (q) => data.suppliers.filter((s) => s.quadrant === q);

  return (
    <div className="mt-10" data-testid="supply-chain-intel">
      <h2 className="ek-eyebrow flex items-center gap-1">
        <Crosshair size={12} /> Supply-chain intelligence
      </h2>
      <p className="mt-1 text-sm text-ek-ink/65">
        Kraljic criticality segmentation, single-point-of-failure detection and composite risk scoring.
      </p>

      {/* Summary */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat icon={Network} label="Suppliers scored" value={data.summary.total} />
        <Stat icon={Crosshair} label="Strategic" value={data.summary.strategic_count} />
        <Stat icon={TriangleAlert} label="Single points of failure" value={data.summary.spof_count} accent={data.summary.spof_count > 0} />
        <Stat icon={TriangleAlert} label="High composite (≥60)" value={data.summary.high_composite} accent={data.summary.high_composite > 0} />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {/* Kraljic matrix */}
        <div className="border border-black/5 bg-white p-5" data-testid="kraljic-matrix">
          <h3 className="font-display text-base font-semibold text-ek-ink">Kraljic matrix</h3>
          <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-ek-ink/65">
            <span>↑ Business impact</span><span>·</span><span>Supply risk →</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {["leverage", "strategic", "routine", "bottleneck"].map((q) => {
              const items = byQuad(q);
              const meta = QUADRANTS[q];
              return (
                <div key={q} className={`border p-3 ${meta.cls}`} data-testid={`quadrant-${q}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-ek-ink">
                      <span className={`h-2 w-2 rounded-full ${meta.dot}`} /> {meta.label}
                    </span>
                    <span className="text-sm font-bold text-ek-ink">{items.length}</span>
                  </div>
                  <p className="mt-0.5 text-[10px] text-ek-ink/65">{meta.desc}</p>
                  <div className="mt-2 space-y-0.5">
                    {items.slice(0, 3).map((s) => (
                      <button
                        key={s.supplier_id}
                        onClick={() => onSelect?.(s.supplier_id)}
                        className="block w-full truncate text-left text-[11px] text-ek-ink/70 hover:text-ek-purple"
                      >
                        {s.legal_name}
                      </button>
                    ))}
                    {items.length > 3 && <p className="text-[10px] text-ek-ink/35">+{items.length - 3} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SPOFs + concentration */}
        <div className="space-y-4">
          <div className="border border-black/5 bg-white p-5" data-testid="spof-list">
            <h3 className="flex items-center gap-1.5 font-display text-base font-semibold text-ek-ink">
              <TriangleAlert size={15} className="text-amber-500" /> Single points of failure
            </h3>
            {data.spofs.length === 0 ? (
              <p className="mt-2 text-xs text-ek-ink/65">No concentration single-points-of-failure detected.</p>
            ) : (
              <div className="mt-3 space-y-1.5">
                {data.spofs.slice(0, 5).map((s) => (
                  <button
                    key={s.supplier_id}
                    onClick={() => onSelect?.(s.supplier_id)}
                    data-testid={`spof-${s.supplier_id}`}
                    className="flex w-full items-center justify-between gap-2 border-l-2 border-amber-400 bg-amber-50/40 px-2.5 py-1.5 text-left transition-colors hover:bg-amber-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-ek-ink">
                        {s.legal_name} {s.sanctioned && <span className="text-red-600">· sanctioned</span>}
                      </p>
                      <p className="truncate text-[10px] text-ek-ink/65">{s.spof_reason}</p>
                    </div>
                    <span className={`shrink-0 text-sm font-bold ${compositeColor(s.composite_score)}`}>{s.composite_score}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border border-black/5 bg-white p-5" data-testid="country-concentration">
            <h3 className="flex items-center gap-1.5 font-display text-base font-semibold text-ek-ink">
              <Globe2 size={15} className="text-ek-purple" /> Country concentration
            </h3>
            <div className="mt-3 space-y-1.5">
              {data.country_concentration.slice(0, 6).map((c) => (
                <div key={c.country} className="flex items-center gap-2">
                  <span className="w-7 text-xs font-medium text-ek-ink/60">{c.country}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-sm bg-ek-canvas">
                    <div
                      className={`h-full ${c.level === "high" ? "bg-red-500" : c.level === "elevated" ? "bg-amber-500" : "bg-ek-purple/50"}`}
                      style={{ width: `${Math.max(4, c.pct * 3)}%` }}
                    />
                  </div>
                  <span className="w-9 text-right text-[11px] text-ek-ink/65">{c.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Auto-raised third-party risks */}
      <div className="mt-4 border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.04] to-transparent p-5" data-testid="auto-raised-risks">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 font-display text-base font-semibold text-ek-ink">
            <Zap size={15} className="text-ek-purple" /> Auto-raised third-party risks
          </h3>
          {canRaise && (
            <button
              onClick={runScan}
              disabled={scanning}
              data-testid="auto-raise-scan-button"
              className="ek-btn-outline !py-1.5 text-xs disabled:opacity-50"
            >
              <RefreshCw size={13} className={scanning ? "animate-spin" : ""} /> Re-scan
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-ek-ink/65">
          Suppliers crossing the composite threshold (or flagged as a single point of failure) automatically
          raise a risk and propose the Supplier Disruption playbook.
        </p>
        {!autoRaised || autoRaised.count === 0 ? (
          <p className="mt-3 text-xs text-ek-ink/65">No third-party risks auto-raised yet.</p>
        ) : (
          <div className="mt-3 space-y-1.5">
            {autoRaised.items.map((r) => (
              <button
                key={r.risk_id}
                onClick={() => onSelect?.(r.supplier_id)}
                data-testid={`auto-raised-${r.risk_id}`}
                className="flex w-full items-center justify-between gap-2 border-l-2 border-ek-purple/50 bg-white px-2.5 py-1.5 text-left transition-colors hover:bg-ek-purple/[0.04]"
              >
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-ek-ink">{r.title}</p>
                  <p className="truncate text-[10px] text-ek-ink/65">
                    {r.quadrant} · {r.auto_reason}
                  </p>
                </div>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className={`text-sm font-bold ${compositeColor(r.composite_score)}`}>{r.composite_score}</span>
                  <ShieldAlert size={13} className="text-ek-purple/60" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="border border-black/5 bg-white px-4 py-3">
    <Icon size={15} className={accent ? "text-red-500" : "text-ek-purple"} />
    <p className={`mt-1.5 font-display text-2xl font-bold ${accent ? "text-red-600" : "text-ek-ink"}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-ek-ink/65">{label}</p>
  </div>
);
